import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import type { TimelineItem, TicketAttachment, TicketMentionResult } from '../types';
import { getStatusLabel } from '../types';
import type { TicketStatus } from '../types';
import { Send, X, Download, FileText, Archive, Mic, Image, Plus, Check, CheckCheck, CornerUpLeft, Forward, Trash2, MoreVertical, MessageCircle, AtSign, ArrowRight, Users, Headphones, Lock, HelpCircle, MonitorUp } from 'lucide-react';
import MediaCapture from './media/MediaCapture';
import { createSendGate, retryTarget, runGuarded, uploadAll } from './chatSendFlow';
import VoiceRecorder from './VoiceRecorder';
import CameraCapture from './CameraCapture';
import QuickRepliesBar from './QuickRepliesBar';
import { useQuickRepliesKeyboard } from './useQuickRepliesKeyboard';
import { AuthenticatedImage, AuthenticatedDownloadLink } from './AuthenticatedImage';
import AttachmentLightbox from './AttachmentLightbox';
import { batchPercent, dragHasFiles, filesFromTransfer, splitBySize, MEDIA_MAX_BYTES } from '../utils/attachPasteDrop';
import AttachmentMedia from './media/AttachmentMedia';
import { ATTACHMENT_TILES_CLASS } from './media/attachmentTiles';
import { attachmentKind, isVoiceRecording } from './media/mediaModel';
import { downloadAuthenticatedFile, fetchAuthenticatedStorage } from '../utils/authenticatedStorage';
import { useTaskStatuses } from '../hooks/useTaskStatuses';
import ConfirmModal from './ConfirmModal';
import Dialog from './ui/Dialog';
import StateBlock from './ui/StateBlock';
import MenuButton, { useTouchMenus } from './table/MenuButton';
import { announce } from './ui/LiveAnnouncer';
import {
  SEND_KEY_STORAGE, isSendKeystroke, modKeyLabel, readDraft, resolveSendKey, writeDraft,
  DEFAULT_COMPOSE_MODE, canWriteInternal, isInternalSend, messageActionsFor,
  DEFAULT_TASK_COMPOSE_MODE, TASK_COMPOSE_MODES, taskSendFlags, questionRecipientsLine, type TaskComposeMode,
  type ComposerAudience, type ComposeMode, type SendKey,
} from './ticketDetail/composerModel';
import PendingDecision from './ui/PendingDecision';
import ReplyActionsBar, { ReplyNoticeLine } from './ticketDetail/ReplyActionsBar';
import { useMediaQuery } from './ui/useOverlay';
import {
  FADING_LINE_MS, MAX_REPLY_BOX_MINUTES, replyBoxCapped, replyBoxDefault, replyLogAction, replySecondsToSend, sentNotice, showReplyActions, showReplyBox, type ReplyNotice,
} from './ticketDetail/replyTimerModel';
import { askWorkEngagement } from './timer/timerFlow';
import { useStartTimer } from './home/useHomeData';
import type { ActiveTimer } from '../types';
import { toast } from '../store/toastStore';
import { formatMessageTime, shouldShowDateSeparator } from './chat/chatBubbleModel';
import { ChatDaySeparator } from './chat/ChatBubble';
import NewMessagePill from './ui/NewMessagePill';
import { useNewMessagePill } from '../hooks/useNewMessagePill';
import ClaudeBadge from './claude/ClaudeBadge';
import { useClaudeMarks, isClaudeMarked } from './claude/useClaudeMarks';

function isImageMime(mime: string): boolean {
  return ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(mime);
}

function isAudioMime(mime: string): boolean {
  return mime.startsWith('audio/') || mime === 'video/webm;codecs=opus';
}

// P-59a (D-235 §1): a screen recording (video/webm, video/mp4) plays in the conversation.
function isVideoMime(mime: string): boolean {
  return mime.toLowerCase().startsWith('video/');
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function AudioBubble({ att, isOwn }: { att: TicketAttachment; isOwn: boolean }) {
  // FIX-AUDIO: the same in-page player + download chip for every audio file (voice note, mp3/m4a/ogg, a type-less file).
  return (
    <div className={`inline-flex min-w-0 max-w-full items-center gap-2 px-3 py-2 rounded-lg text-xs ${isOwn ? 'bg-surface-0/70 border border-primary-100' : 'bg-surface-50 border border-surface-200'}`}>
      <AttachmentMedia
        name={att.original_name}
        mime={att.mime_type}
        size={fmtBytes(att.file_size_bytes)}
        load={() => fetchAuthenticatedStorage(att.url)}
        onDownload={() => void downloadAuthenticatedFile(att.url, att.original_name)}
        fallback={null}
      />
    </div>
  );
}

function ImageOnlyAttachment({ att }: { att: TicketAttachment }) {
  const [enlarged, setEnlarged] = useState(false);
  return (
    <>
      <AuthenticatedImage
        storageUrl={att.url}
        lazy
        alt={att.original_name}
        className="max-w-72 max-h-60 object-cover cursor-pointer hover:opacity-90 transition-opacity"
        onClick={() => setEnlarged(true)}
      />
      {enlarged && <AttachmentLightbox att={att} onClose={() => setEnlarged(false)} />}
    </>
  );
}

function VideoBubble({ att }: { att: TicketAttachment; isOwn: boolean }) {
  // The same authenticated /storage fetch as the download link; the player loads on click.
  return (
    <AttachmentMedia
      name={att.original_name}
      mime={att.mime_type}
      size={fmtBytes(att.file_size_bytes)}
      load={() => fetchAuthenticatedStorage(att.url)}
      onDownload={() => void downloadAuthenticatedFile(att.url, att.original_name)}
      fallback={null}
    />
  );
}

function AttachmentBubble({ att, isOwn }: { att: TicketAttachment; isOwn: boolean }) {
  const [enlarged, setEnlarged] = useState(false);
  // FIX-B38 (QX-800): a voice recording is an audio bubble even when stored as video/webm.
  if (isVoiceRecording(att.mime_type, att.original_name)) {
    return <AudioBubble att={att} isOwn={isOwn} />;
  }
  if (isVideoMime(att.mime_type)) {
    return <VideoBubble att={att} isOwn={isOwn} />;
  }

  if (isAudioMime(att.mime_type) || attachmentKind(att.mime_type, att.original_name) === 'audio') {
    return <AudioBubble att={att} isOwn={isOwn} />;
  }

  if (isImageMime(att.mime_type)) {
    return (
      <>
        <AuthenticatedImage
          storageUrl={att.url}
          lazy
          alt={att.original_name}
          className="max-w-64 max-h-52 rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => setEnlarged(true)}
        />
        {enlarged && <AttachmentLightbox att={att} onClose={() => setEnlarged(false)} />}
      </>
    );
  }

  return (
    <AuthenticatedDownloadLink
      storageUrl={att.url}
      filename={att.original_name}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors border ${
        isOwn
          ? 'bg-surface-0/70 border-primary-100 hover:bg-surface-0 text-surface-800'
          : 'bg-surface-50 border-surface-200 hover:bg-surface-100 text-surface-700'
      }`}
    >
      {att.mime_type === 'application/pdf' ? <FileText size={14} /> :
        att.mime_type.includes('zip') ? <Archive size={14} /> : <FileText size={14} />}
      <span className="max-w-36 truncate">{att.original_name}</span>
      <span className="text-surface-600 tabular-nums">{fmtBytes(att.file_size_bytes)}</span>
      <Download size={12} />
    </AuthenticatedDownloadLink>
  );
}

/** D-317 §3 — the ticket's files as uniform tiles in a row (the same viewers as in the conversation). */
export function AttachmentTiles({ files, label }: { files: TicketAttachment[]; label: string }) {
  return (
    <ul aria-label={label} className={ATTACHMENT_TILES_CLASS} data-testid="attachment-tiles">
      {files.map((att) => (
        <li key={att.id} className="flex min-w-0 flex-col gap-1">
          <AttachmentBubble att={att} isOwn={false} />
          {isImageMime(att.mime_type) && (
            <span className="truncate text-xs text-text-2"><bdi>{att.original_name}</bdi> · <span className="tabular-nums">{fmtBytes(att.file_size_bytes)}</span></span>
          )}
        </li>
      ))}
    </ul>
  );
}

function PendingImagePreview({ file, onRemove }: { file: File; onRemove: () => void }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string>('');
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  if (!isImageFile(file)) {
    return (
      <div className="relative group">
        <div className="w-16 h-16 rounded-lg bg-surface-100 flex items-center justify-center border border-surface-200">
          <FileText size={20} className="text-surface-500" />
        </div>
        <button type="button" onClick={onRemove} aria-label={`${t('common.remove')}: ${file.name}`}
          className="absolute -top-2 -end-2 w-6 h-6 bg-surface-0 text-surface-700 border border-surface-200 rounded-full flex items-center justify-center shadow-elevated hover:text-danger-700">
          <X size={12} aria-hidden="true" />
        </button>
        <p className="text-xs text-surface-600 mt-0.5 max-w-16 truncate text-center">{file.name}</p>
      </div>
    );
  }

  return (
    <div className="relative group">
      <img src={url} alt={file.name}
        className="w-16 h-16 rounded-lg object-cover border border-surface-200" />
      <button type="button" onClick={onRemove} aria-label={`${t('common.remove')}: ${file.name}`}
        className="absolute -top-2 -end-2 w-6 h-6 bg-surface-0 text-surface-700 border border-surface-200 rounded-full flex items-center justify-center shadow-elevated hover:text-danger-700">
        <X size={12} aria-hidden="true" />
      </button>
    </div>
  );
}

interface Props {
  /** D-265 round 4: the conversation of a task of an engagement in the archive - read only (no composer). */
  readOnly?: boolean;
  timeline: TimelineItem[];
  ticketId: string;
  className?: string;
  /** When set, @-mention lists only tickets in the same chat group (same customer / discussion team) */
  mentionScope?: 'all' | 'chat_group';
  /**
   * Who reads what is sent (T-44): shown above the composer ("ההודעה תישלח
   * ללקוח · ‹name›") and it picks the default send key (Ctrl/⌘+Enter when a
   * customer reads it). Omitted: no line, Enter sends (the conversations view).
   */
  audience?: ComposerAudience;
  /**
   * Phones (< md): the messages flow with the page and the composer docks to the
   * bottom of the screen, above the tab bar, while the conversation is in view
   * (`.composer-dock`, index.css). The ticket page uses it; desktop is unchanged.
   */
  dockComposer?: boolean;
  /** D-146 — the row's task_type: a service request's status events read "בטיפול" / "הושלמה". */
  taskType?: string | null;
  /** P-45b (D-210) — a discussion opened as a conversation; with `hasCustomer` it decides the reply-time box default (QX-215). */
  isConversation?: boolean;
  hasCustomer?: boolean;
  /** FIX-D251 §1 (D-251) — a task of a customer: [💬 בצוות] / [❓ שאלה ללקוח] / [🔒 הערה פנימית]. */
  customerTask?: boolean;
  /**
   * P-37 (D-194) — the service ticket's "אני על זה" / "יצירת משימה מהקריאה" bar
   * next to the composer (the ticket page passes it; tasks never do).
   */
  replyBar?: { title: string; status: string; cancelled: boolean; onCreateTask?: () => void; onForward?: () => void };
  /**
   * FIX-B43 (FQ-060, D-235 §1) - the request's opening: its description and the files attached when it was
   * opened (ticket-level, no comment). Shown as the FIRST bubble, with the same viewers as a message's files.
   * Rendered only when there are files; omitted/empty = the conversation is unchanged.
   */
  opening?: { files: TicketAttachment[]; description?: string | null; authorName?: string | null; createdAt: string };
}

function ChatForwardModal({
  open,
  sourceTicketId,
  commentId,
  onClose,
  onSuccess,
  t,
}: {
  open: boolean;
  sourceTicketId: string;
  commentId: string;
  onClose: () => void;
  onSuccess: () => void;
  t: (k: string) => string;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['conversations-forward-picker'],
    queryFn: () => api.getConversations({ limit: 80 }),
    enabled: open,
  });

  const conversations = (data?.data ?? []).filter((c) => c.id !== sourceTicketId);

  const handleForward = async (targetId: string) => {
    setSubmitting(true);
    setError('');
    try {
      await api.forwardChatMessage({
        source_ticket_id: sourceTicketId,
        comment_id: commentId,
        target_ticket_id: targetId,
      });
      onSuccess();
      onClose();
    } catch {
      setError(t('ticketDetail.composer.forwardFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={t('ticketDetail.composer.forwardTo')} size="md" bodyClassName="px-0 pb-2">
      {error && <p className="px-5 py-2 text-sm text-danger-700" role="alert">{error}</p>}
      {isLoading && <StateBlock kind="loading" size="panel" rows={4} />}
      {!isLoading && conversations.length === 0 && (
        <p className="text-center text-text-2 text-sm py-8">{t('conversations.empty')}</p>
      )}
      {conversations.length > 0 && (
        <ul className="divide-y divide-border">
          {conversations.map((conv) => (
            <li key={conv.id}>
              <button
                type="button"
                disabled={submitting}
                onClick={() => void handleForward(conv.id)}
                className="w-full px-5 py-3 text-start hover:bg-surface-subtle transition-colors duration-micro disabled:opacity-50"
              >
                <bdi className="font-medium text-text block truncate">{conv.customer_name || conv.title}</bdi>
                <bdi className="text-xs text-text-2 truncate block">{conv.title}</bdi>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,audio/*';

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

// ----------------------------------------------------------------------------
// Phase K3: @-mention support
// We store ticket references inline using a plain-text token:
//   @@ticket:<UUID>|<TITLE>@@
// This survives the existing comment.content column without any backend change
// and is parsed at render time into a clickable chip.
// ----------------------------------------------------------------------------
const MENTION_TOKEN_RE = /@@ticket:([0-9a-fA-F-]{36})\|([^@]*?)@@/g;

function buildMentionToken(id: string, title: string): string {
  // Strip @@ from the title so the token cannot be broken
  const safeTitle = title.replace(/@@/g, '').slice(0, 80);
  return `@@ticket:${id}|${safeTitle}@@`;
}

/**
 * Look for a pending `@query` token immediately before the caret.
 * Returns the search text + the start offset of the `@` if found.
 */
function detectMentionTrigger(value: string, caret: number): { query: string; start: number } | null {
  // Walk backwards from the caret to find a recent `@` that is not preceded by a word char
  let i = caret - 1;
  while (i >= 0) {
    const ch = value[i];
    if (ch === '@') {
      const prev = i > 0 ? value[i - 1] : ' ';
      // Only trigger at start-of-string or after whitespace, so `email@x` doesn't fire
      if (i === 0 || /\s/.test(prev)) {
        const query = value.slice(i + 1, caret);
        // Bail out if the candidate contains whitespace, a newline, or @ — not a single mention token
        if (/[\s@]/.test(query)) return null;
        return { query, start: i };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i--;
  }
  return null;
}

function MentionAutocomplete({
  items,
  highlight,
  onPick,
  onHover,
  t,
  loading,
}: {
  items: TicketMentionResult[];
  highlight: number;
  onPick: (item: TicketMentionResult) => void;
  onHover: (idx: number) => void;
  t: (k: string) => string;
  loading: boolean;
}) {
  return (
    <div className="absolute bottom-full mb-1 inset-x-0 bg-surface-0 border border-surface-200 rounded-lg shadow-elevated overflow-hidden z-30 max-h-64 overflow-y-auto">
      <div className="px-3 py-1.5 border-b border-surface-200 text-xs text-surface-600 flex items-center gap-1">
        <AtSign size={12} /> {t('conversations.mentionHint')}
      </div>
      {loading && <div className="px-3 py-3 text-xs text-surface-500 text-center">…</div>}
      {!loading && items.length === 0 && (
        <div className="px-3 py-3 text-xs text-surface-500 text-center">{t('conversations.mentionEmpty')}</div>
      )}
      {items.map((it, idx) => (
        <button
          key={it.id}
          type="button"
          onMouseEnter={() => onHover(idx)}
          onMouseDown={(e) => { e.preventDefault(); onPick(it); }}
          className={`w-full px-3 py-2 text-start text-sm flex items-start gap-2 transition-colors ${
            idx === highlight ? 'bg-surface-100' : 'hover:bg-surface-50'
          }`}
        >
          <span className="flex-shrink-0 mt-0.5 text-surface-500">
            <MessageCircle size={14} />
          </span>
          <span className="flex-1 min-w-0 text-start">
            <span className="block text-surface-900 font-medium truncate">
              #{it.ticket_number} · {it.title || t('conversations.chat')}
            </span>
            {it.customer_name && (
              <span className="block text-xs text-surface-600 truncate">{it.customer_name}</span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * Render comment content with inline mention chips.
 * The plain-text tokens become buttons that navigate the conversations view
 * to the referenced ticket (or any consumer can pass a navigate handler).
 */
function renderCommentContent(
  raw: string,
  onMentionClick: (id: string) => void,
  t: (k: string) => string,
): React.ReactNode {
  if (!raw) return null;
  if (!raw.includes('@@ticket:')) {
    return <span className="whitespace-pre-wrap break-words">{raw}</span>;
  }
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  // Each test iteration captures one token; reset regex state at the start
  MENTION_TOKEN_RE.lastIndex = 0;
  while ((m = MENTION_TOKEN_RE.exec(raw)) !== null) {
    const before = raw.slice(lastIdx, m.index);
    if (before) parts.push(<span key={`t-${lastIdx}`}>{before}</span>);
    const id = m[1];
    const title = m[2] || t('conversations.chat');
    parts.push(
      <button
        key={`m-${m.index}`}
        type="button"
        onClick={(e) => { e.stopPropagation(); onMentionClick(id); }}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded-xs bg-primary-50 text-primary-800 hover:bg-primary-100 text-xs font-medium align-baseline"
        title={t('conversations.openLinkedTicket')}
      >
        <MessageCircle size={12} />
        {title}
      </button>
    );
    lastIdx = m.index + m[0].length;
  }
  const after = raw.slice(lastIdx);
  if (after) parts.push(<span key={`t-end`}>{after}</span>);
  return <span className="whitespace-pre-wrap break-words">{parts}</span>;
}

/** D-146 (Q-97): the yellow "🔒 הערה פנימית · רק עובדי OTE רואים" tag on an internal message. */
function InternalNoteTag({ t }: { t: (k: string) => string }) {
  return (
    <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-warning-800">
      <Lock size={12} aria-hidden="true" />
      {t('ticketDetail.composer.mode.internal')}
      <span aria-hidden="true">·</span>
      <span className="font-normal">{t('ticketDetail.composer.mode.internalOnly')}</span>
    </p>
  );
}

function MessageReceiptTicks({
  status,
  t,
}: {
  status?: 'sent' | 'delivered' | 'read' | null;
  t: (k: string) => string;
}) {
  if (!status || status === 'sent') {
    return <Check size={12} className="text-surface-600" aria-label={t('chat.receiptSent')} />;
  }
  if (status === 'delivered') {
    return <CheckCheck size={12} className="text-surface-600" aria-label={t('chat.receiptDelivered')} />;
  }
  return <CheckCheck size={12} className="text-info-700" aria-label={t('chat.receiptRead')} />;
}


function readSendKey(): string | null {
  try { return localStorage.getItem(SEND_KEY_STORAGE); } catch { return null; }
}

function safeStorage(): Storage | null {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

export default function TicketChatView({ timeline, ticketId, className, mentionScope = 'all', audience, dockComposer = false, taskType, isConversation = false, hasCustomer = false, replyBar, opening, customerTask = false, readOnly = false }: Props) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const attachRef = useRef<HTMLDivElement>(null);
  const attachButtonRef = useRef<HTMLButtonElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  // FQ-040: on phones the docked composer keeps only the input; the reply notice and the
  // reply-action buttons scroll with the page, so the conversation gets the screen.
  const isPhone = useMediaQuery('(max-width: 767px)');
  // D-265 round 4: read only -> no reply bar outside the dock either.
  const extrasOutsideDock = dockComposer && isPhone && !readOnly;
  // A docked composer publishes its height, so the floating timer capsule and
  // toasts can sit above it on phones (`--composer-dock-h`, index.css).
  useEffect(() => {
    const el = composerRef.current;
    if (!dockComposer || !el || typeof ResizeObserver === 'undefined') return undefined;
    const root = document.documentElement;
    const ro = new ResizeObserver(() => root.style.setProperty('--composer-dock-h', `${Math.round(el.offsetHeight)}px`));
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--composer-dock-h');
    };
  }, [dockComposer]);

  // Draft per ticket (T-44, PRD §6.3): restored on open, saved quietly while typing, cleared on send.
  const [content, setContent] = useState(() => readDraft(safeStorage(), ticketId));
  const [draftSaved, setDraftSaved] = useState(false);
  const draftOwner = useRef(ticketId);
  const contentRef = useRef(content);
  contentRef.current = content;
  const touch = useTouchMenus();
  const { labelOf } = useTaskStatuses();
  const [sendKey, setSendKey] = useState<SendKey>(() => resolveSendKey(readSendKey(), audience ?? { kind: 'team' }));
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // R-02: upload progress, drag-over highlight, and the comment whose files failed (a retry attaches to it, no second message).
  const [uploadInfo, setUploadInfo] = useState<{ n: number; total: number; pct: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);
  const retryRef = useRef<{ commentId: string; ticketId: string } | null>(null);
  // R-02c: Enter / the button / a second tab-press during an upload must not send a second message.
  const sendGate = useRef(createSendGate());
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  // R-02 round 2: screenshot / screen recording through the shared MediaCapture (as in the portal).
  const [showCapture, setShowCapture] = useState(false);
  const [capturePending, setCapturePending] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [quickReplyHighlight, setQuickReplyHighlight] = useState(0);
  const [replyingTo, setReplyingTo] = useState<TimelineItem | null>(null);
  const [forwardCommentId, setForwardCommentId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string } | null>(null);
  const [actionError, setActionError] = useState('');
  // D-146 (Q-97): [💬 תשובה ללקוח] (default) or [🔒 הערה פנימית].
  const [composeMode, setComposeMode] = useState<ComposeMode>(DEFAULT_COMPOSE_MODE);
  // FIX-D251 §1 — the customer task's own choice (its own state: the ticket composer is unchanged).
  const [taskMode, setTaskMode] = useState<TaskComposeMode>(DEFAULT_TASK_COMPOSE_MODE);
  const offerInternal = canWriteInternal(audience);
  const taskModes = customerTask && offerInternal;
  const internalMode = taskModes ? taskMode === 'internal' : isInternalSend(composeMode, audience);
  // FIX-D251b §3 — who a question to the customer really reaches (the customer's people with portal access).
  const questionMode = taskModes && taskMode === 'question';
  const { data: questionRecipientsRes } = useQuery({
    queryKey: ['task-question-recipients', ticketId],
    queryFn: () => api.getTaskQuestionRecipients(ticketId),
    enabled: questionMode,
  });
  const questionLine = questionMode ? questionRecipientsLine(questionRecipientsRes?.data?.map((r) => r.name)) : null;
  const questionBlocked = questionLine?.blocked === true;
  // D-317 §5 — no "מחכה לתשובה" here any more: "ממתינה ללקוח" is set from the status button at the top.

  // ---- P-37 (D-194) — reply time ----------------------------------------
  const isStaffUser = user?.role === 'admin' || user?.role === 'agent';
  const { data: timerStatus } = useQuery({
    queryKey: ['timer-status'],
    queryFn: () => api.getTimerStatus(),
    enabled: isStaffUser && (taskType === 'ticket' || taskType === 'discussion'),
    staleTime: 10_000,
  });
  const replyTimer: ActiveTimer | null = (timerStatus?.data?.timers ?? [])
    .find((tm: ActiveTimer) => tm.ticket_id === ticketId && tm.kind === 'reply') ?? null;
  // The composer's "טיימר מענה" (Q-174 / Q-178): checked on a ticket, unchecked on a discussion.
  const [replyBoxOn, setReplyBoxOn] = useState(() => replyBoxDefault({ taskType, isConversation, hasCustomer }));
  const replyStartedAt = useRef<number | null>(null);
  const replyBoxShown = showReplyBox({ isStaff: isStaffUser, taskType, internalMode, replyTimerRunning: !!replyTimer, employeesOnlyChat: isConversation && !hasCustomer });
  /**
   * D-317 §4 — a service ticket's composer (staff): ONE box. On top the tabs "תשובה ללקוח" /
   * "הערה פנימית" and "to whom"; the message; below: attach · record · "טיימר מענה" (a tag) · send
   * (Enter in its tooltip; the switch stays). Layout only: the same controls and handlers.
   */
  const ticketComposer = offerInternal && !taskModes && taskType === 'ticket';
  const sendKeyText = `${sendKey === 'enter' ? 'Enter' : `${modKeyLabel()}+Enter`} ${t('ticketDetail.composer.sendKey.toSend')}`;
  /** The first sign of a reply (typing, a recording, a file) starts the count. */
  const markReplyStarted = () => {
    if (replyStartedAt.current === null) replyStartedAt.current = Date.now();
  };
  useEffect(() => {
    setReplyBoxOn(replyBoxDefault({ taskType, isConversation, hasCustomer }));
    replyStartedAt.current = null;
  }, [ticketId, taskType, isConversation, hasCustomer]);
  // Q-180 / Q-182 — the short line above the composer that fades by itself.
  const replyRunning = !!replyTimer;
  const [notice, setNotice] = useState<ReplyNotice | null>(null);
  useEffect(() => {
    if (!notice) return undefined;
    // D-252 — the "paused task" line (with "להריץ את שתיהן") stays while this "אני על זה" runs (or until closed by hand);
    // once it no longer runs, it still stays 10 seconds.
    if (notice.kind === 'paused' && replyRunning) return undefined;
    const tm = setTimeout(() => setNotice(null), FADING_LINE_MS);
    return () => clearTimeout(tm);
  }, [notice, replyRunning]);
  const restartReply = useStartTimer({ onStarted: () => setNotice(null) });

  // @-mention picker state
  const [mention, setMention] = useState<{ query: string; start: number } | null>(null);
  const [mentionHighlight, setMentionHighlight] = useState(0);

  const { data: mentionData, isLoading: mentionLoading } = useQuery({
    queryKey: ['mention-search', ticketId, mentionScope, mention?.query ?? ''],
    queryFn: () => api.ticketMentionSearch({
      context_ticket_id: ticketId,
      q: mention?.query || undefined,
      limit: 8,
      scope: mentionScope,
    }),
    enabled: mention !== null,
    staleTime: 5_000,
  });
  const mentionItems: TicketMentionResult[] = mentionData?.data ?? [];

  useEffect(() => {
    if (mentionHighlight >= mentionItems.length) setMentionHighlight(0);
  }, [mentionItems.length, mentionHighlight]);

  // Switching to another ticket (conversations view): keep the old draft, load the new one.
  useEffect(() => {
    if (draftOwner.current === ticketId) return;
    writeDraft(safeStorage(), draftOwner.current, contentRef.current);
    draftOwner.current = ticketId;
    setComposeMode(DEFAULT_COMPOSE_MODE);
    setTaskMode(DEFAULT_TASK_COMPOSE_MODE);
    setContent(readDraft(safeStorage(), ticketId));
    setDraftSaved(false);
  }, [ticketId]);

  // Save the draft 600 ms after typing stops; say so quietly.
  useEffect(() => {
    const owner = draftOwner.current;
    if (owner !== ticketId) return;
    const stored = readDraft(safeStorage(), owner);
    if (stored === content) return;
    const timer = setTimeout(() => {
      const ok = writeDraft(safeStorage(), owner, content);
      setDraftSaved(ok && content.trim().length > 0);
    }, 600);
    return () => clearTimeout(timer);
  }, [content, ticketId]);

  // Leaving the page saves what is there at once.
  useEffect(() => () => { writeDraft(safeStorage(), draftOwner.current, contentRef.current); }, []);

  const toggleSendKey = () => {
    const next: SendKey = sendKey === 'enter' ? 'mod-enter' : 'enter';
    setSendKey(next);
    try { localStorage.setItem(SEND_KEY_STORAGE, next); } catch { /* not remembered in private mode */ }
    announce(next === 'enter' ? t('ticketDetail.composer.sendKey.nowEnter') : t('ticketDetail.composer.sendKey.nowMod', { mod: modKeyLabel() }));
  };

  // Attach menu: an outside press or Escape closes it (no full-screen click catcher).
  useEffect(() => {
    if (!showAttachMenu) return;
    const onDown = (e: PointerEvent) => {
      if (attachRef.current && !attachRef.current.contains(e.target as Node)) setShowAttachMenu(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setShowAttachMenu(false); attachButtonRef.current?.focus(); }
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown, true); document.removeEventListener('keydown', onKey); };
  }, [showAttachMenu]);

  const insertMention = useCallback((item: TicketMentionResult) => {
    if (!mention) return;
    const ta = textareaRef.current;
    const before = content.slice(0, mention.start);
    const after = content.slice((ta?.selectionStart ?? content.length));
    const token = buildMentionToken(item.id, item.title || `#${item.ticket_number}`);
    const next = `${before}${token} ${after}`;
    setContent(next);
    setMention(null);
    setMentionHighlight(0);
    setTimeout(() => {
      if (ta) {
        const pos = (before + token + ' ').length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
      }
    }, 0);
  }, [mention, content]);

  const handleMentionClick = useCallback((id: string) => {
    navigate(`/tickets/${id}?tab=chat`);
  }, [navigate]);

  // P-60 (D-236): follow new messages only when the reader is at the bottom (or sent it);
  // otherwise show "הודעה חדשה ↓" instead of jumping the scroll.
  const lastTl = timeline[timeline.length - 1];
  const { pending: newPending, jump: jumpToNewest } = useNewMessagePill(
    messagesRef, timeline.length, !!lastTl && !!user && lastTl.actor_id === user.id, true, ticketId,
  );

  useEffect(() => {
    api.markTicketChatRead(ticketId).then(() => {
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    }).catch(() => { /* non-blocking */ });
  }, [ticketId, queryClient]);

  const autoResizeTextarea = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, []);

  const submitInner = async () => {
    const hasContent = content.trim().length > 0;
    const hasFiles = pendingFiles.length > 0;
    if (!hasContent && !hasFiles) return;
    if (questionBlocked) return;
    // R-02: a screen recording that was started and not attached must not be dropped silently by a send.
    if (capturePending) { setError(t('media.pendingRecording')); return; }

    setSubmitting(true);
    setError('');
    try {
      // R-02: files that failed to upload are retried on the SAME message (no duplicate message).
      const retry = retryRef.current;
      const retryCommentId = retryTarget(retry, ticketId, hasContent, hasFiles);
      if (retryCommentId) {
        const failed = await uploadFiles(retryCommentId, pendingFiles);
        setPendingFiles(failed);
        if (failed.length) {
          setError(t('media.uploadRetry', { name: failed.map((f) => f.name).join(', ') }));
          return;
        }
        retryRef.current = null;
        queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        jumpToNewest();
        return;
      }
      retryRef.current = null;
      const text = hasContent ? content.trim() : (hasFiles ? '📎' : '');
      // P-37 (Q-174) — the reply time of the composer (none under a minute / box off).
      const replySeconds = replyBoxShown
        ? replySecondsToSend({ checked: replyBoxOn, startedAt: replyStartedAt.current, now: Date.now() })
        : undefined;
      const send = (engagementId?: string) => api.createComment(ticketId, {
        content: text,
        is_internal: internalMode,
        ...(taskModes ? taskSendFlags(taskMode) : {}),
        // An internal note is never a quote of a customer message (the server refuses it).
        reply_to_comment_id: internalMode ? undefined : (replyingTo?.comment_id || replyingTo?.id),
        ...(replySeconds ? { reply_seconds: replySeconds } : {}),
        ...(engagementId ? { engagement_id: engagementId } : {}),
      });
      // FIX-P37 — the message is never held back by the reply time: the server sends it first.
      const commentRes = await send();
      replyStartedAt.current = null;
      let stoppedLine = sentNotice(commentRes.data?.reply_timer);
      // Q-178 — no engagement chosen yet: asked ONCE (saved on the ticket), after the send.
      const logAction = replyLogAction(commentRes.data?.reply_log);
      if (logAction.kind === 'ask') {
        const chosen = await askWorkEngagement({ task: { id: ticketId, title: replyBar?.title ?? '' }, engagements: logAction.engagements, mode: 'log' });
        if (chosen && commentRes.data?.id) {
          try {
            await api.recordReplyTime(ticketId, { seconds: logAction.seconds, engagementId: chosen, commentId: commentRes.data.id });
          } catch {
            stoppedLine = { kind: 'unlogged', minutes: logAction.minutes };
          }
        } else {
          stoppedLine = { kind: 'unlogged', minutes: logAction.minutes };
        }
      } else if (logAction.kind === 'unlogged') {
        stoppedLine = { kind: 'unlogged', minutes: logAction.minutes };
      }
      if (stoppedLine) setNotice(stoppedLine);
      // D-198 QX-145 — the reply box stops at 120 minutes per message: a toast (not the bell).
      if (replyBoxCapped(replySeconds)) toast('info', t('replyTimer.boxCapped', { n: MAX_REPLY_BOX_MINUTES }));
      if (commentRes.data?.reply_timer || commentRes.data?.reply_log) {
        queryClient.invalidateQueries({ queryKey: ['timer-status'] });
        queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      }
      const commentId = commentRes.data?.id;
      let failedFiles: File[] = [];
      if (commentId && hasFiles) {
        failedFiles = await uploadFiles(commentId, pendingFiles);
        if (failedFiles.length) {
          // R-02: the files that did not go up stay in the box so the user can retry (they used to vanish silently).
          retryRef.current = { commentId, ticketId };
          setError(t('media.uploadRetry', { name: failedFiles.map((f) => f.name).join(', ') }));
        }
      }
      setContent('');
      contentRef.current = '';
      writeDraft(safeStorage(), ticketId, '');
      setDraftSaved(false);
      setPendingFiles(failedFiles);
      setReplyingTo(null);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      // FIX-D251 §1 — a question to the customer is sent once: back to [💬 בצוות].
      if (taskModes && taskMode === 'question') setTaskMode(DEFAULT_TASK_COMPOSE_MODE);
      jumpToNewest();
    } catch (err) {
      // FIX-D251 §1 — a refused question says why (e.g. no contact with portal access).
      const why = taskModes && taskMode === 'question' ? (err as { error?: { message?: unknown } })?.error?.message : undefined;
      setError(typeof why === 'string' && why ? why : t('ticketDetail.composer.sendFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => { await runGuarded(sendGate.current, submitInner); };

  const uploadFiles = async (commentId: string, files: File[]): Promise<File[]> => {
    const failed = await uploadAll(
      files,
      (file, i) => api.uploadCommentAttachment(ticketId, commentId, file, (loaded, total) => {
        setUploadInfo({ n: i + 1, total: files.length, pct: batchPercent(i, files.length, loaded, total) });
      }),
      (i, total) => setUploadInfo({ n: i + 1, total, pct: batchPercent(i, total, 0, 1) })
    );
    setUploadInfo(null);
    return failed;
  };

  const addFiles = (files: FileList | File[] | null) => {
    if (!files) return;
    // 10 MB for a document / image, 50 MB for a recording (the server applies the same rule).
    const { ok: valid, tooBig } = splitBySize(Array.from(files));
    if (tooBig.length) setError(tooBig.map((b) => t('media.tooLargeNamed', b)).join(' · '));
    if (valid.length > 0) markReplyStarted();
    setPendingFiles((prev) => [...prev, ...valid]);
    setShowAttachMenu(false);
  };

  const { onKeyDown: quickRepliesKeyDown, isOpen: quickRepliesOpen, filtered: quickRepliesFiltered } = useQuickRepliesKeyboard(
    content,
    (text) => {
      setContent(text);
      autoResizeTextarea();
    },
    () => { void submit(); }
  );

  const handleDeleteMessage = async () => {
    if (!deleteTarget) return;
    setActionError('');
    try {
      await api.deleteComment(ticketId, deleteTarget.id);
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    } catch {
      setActionError(t('ticketDetail.composer.deleteFailed'));
      setDeleteTarget(null);
    }
  };

  const chatItems = timeline.filter((item) => {
    // Internal notes reach this view only for OTE staff (the server filters them for anyone else).
    if (item.item_type === 'comment') return true;
    if (item.item_type === 'event' && item.event_type === 'status_changed') return true;
    // B.23 / E.77 — reopen history (explicit or automatic on client activity)
    if (item.item_type === 'event' && item.event_type === 'ticket_reopened') return true;
    return false;
  });
  // FIX-CLAUDE-B (D-314 §11): the "Claude" tag on a message / status change made through Claude (OTE employees only).
  const claudeMarks = useClaudeMarks(chatItems.map((item) => (item.item_type === 'comment' ? item.comment_id || item.id : item.id)));

  const replyNoticeEl = (
    <>
      {/* P-37 (Q-180 / Q-182) — a short line that fades by itself. */}
      {notice && (
        <ReplyNoticeLine
          notice={notice}
          onClose={() => setNotice(null)}
          onStillOnIt={() => restartReply.mutate({ id: ticketId, title: replyBar?.title ?? '', reply: true })}
          onRunBoth={(replyTimerId) => {
            void api.resumePausedTimers(replyTimerId)
              .then(() => { setNotice(null); announce(t('replyTimer.bothRunning')); })
              .catch(() => setError(t('replyTimer.bothFailed')))
              .finally(() => {
                queryClient.invalidateQueries({ queryKey: ['timer-status'] });
                queryClient.invalidateQueries({ queryKey: ['work-logs'] });
              });
          }}
        />
      )}
    </>
  );
  const replyActionsEl = (
    <>
      {/* P-37 (Q-172) — "אני על זה" / "יצירת משימה מהקריאה", next to the composer, prominent. */}
      {replyBar && showReplyActions({ isStaff: isStaffUser, taskType, status: replyBar.status, cancelled: replyBar.cancelled }) && (
        <ReplyActionsBar
          ticketId={ticketId}
          title={replyBar.title}
          replyTimer={replyTimer}
          onCreateTask={replyBar.onCreateTask}
          onForward={replyBar.onForward}
          onNotice={setNotice}
        />
      )}
    </>
  );
  // D-252 — on phones the line with "להריץ את שתיהן" sits in the dock (the buttons stay outside, FQ-040); on desktop both stay together as before.
  const replyExtras = (<>{replyNoticeEl}{replyActionsEl}</>);

  return (
    <div className={`flex flex-col h-full bg-surface-50 ${className ?? ''}`}>
      {/* Messages area */}
      <div ref={messagesRef} className={`flex-1 overflow-y-auto px-3 md:px-4 py-4 min-h-0 ${dockComposer ? 'max-md:flex-none max-md:overflow-visible max-md:min-h-[max(45dvh,calc(100dvh-var(--bottom-chrome)-var(--composer-dock-h,4rem)-6rem))] chat-messages' : ''}`}>
        {chatItems.length === 0 && !(opening && opening.files.length > 0) && (
          <div className="flex flex-col items-center justify-center py-12">
            {/* Empty state per DESIGN_SYSTEM §14: a glyph without a disc. */}
            <Send size={40} strokeWidth={1.5} className="mb-3 text-text-disabled rtl-mirror" aria-hidden="true" />
            <p className="text-surface-800 text-sm font-medium">{t('chat.noMessages')}</p>
            <p className="text-surface-600 text-xs mt-1">{t('chat.startConversation')}</p>
          </div>
        )}
        {opening && opening.files.length > 0 && (
          <div data-testid="opening-bubble" className="mt-1 flex justify-start">
            <div className="max-w-[80%] md:max-w-[65%] rounded-xl rounded-ss-xs border border-surface-200 bg-surface-0 px-3 py-2 text-body-long text-surface-900">
              <div className="mb-0.5 flex items-center gap-2 text-xs">
                {opening.authorName && <span className="font-medium text-surface-700"><bdi>{opening.authorName}</bdi></span>}
                <span className="text-surface-600">{t('chat.openingBubble')}</span>
              </div>
              {opening.description && (
                <p className="whitespace-pre-wrap break-words [unicode-bidi:plaintext]">{opening.description}</p>
              )}
              <div className="mt-1.5 space-y-1.5">
                {opening.files.map((att) => (
                  <AttachmentBubble key={att.id} att={att} isOwn={false} />
                ))}
              </div>
              <div className="mt-1 text-xs tabular-nums text-surface-600">{formatMessageTime(opening.createdAt)}</div>
            </div>
          </div>
        )}
        {chatItems.map((item, idx) => {
          const prevItem = idx > 0 ? chatItems[idx - 1] : null;
          const showDateSep = shouldShowDateSeparator(item.created_at, prevItem?.created_at ?? null);

          if (item.item_type === 'event' && (item.event_type === 'status_changed' || item.event_type === 'ticket_reopened')) {
            // Same labels as the status badge (the controlled task-status list, B.13).
            const statusText = (k: string) => labelOf(k, taskType) || getStatusLabel(k as TicketStatus, t, taskType);
            const from = item.metadata?.from ? statusText(item.metadata.from as string) : '';
            const to = item.metadata?.to ? statusText(item.metadata.to as string) : '';
            const label = item.event_type === 'ticket_reopened'
              ? (item.metadata?.auto ? t('timeTasks.reopen.autoEvent') : t('timeTasks.reopen.event'))
              : t('chat.statusChanged');
            return (
              <div key={item.id}>
                {showDateSep && (
                  <ChatDaySeparator date={item.created_at} t={t} lang={i18n.language || 'he'} />
                )}
                <div className="flex justify-center my-2">
                  <span className="inline-flex items-center gap-1 text-xs text-surface-600 bg-surface-100 px-3 py-1 rounded-full">
                    {label} {from}
                    <ArrowRight size={12} className="rtl-mirror shrink-0" aria-label={t('ticketDetail.composer.statusTo')} />
                    {to}
                    {isClaudeMarked(claudeMarks, item.id) && <ClaudeBadge className="ms-1" />}
                  </span>
                </div>
              </div>
            );
          }

          const isOwnMessage = item.actor_id === user?.id;
          const isDeleted = item.is_deleted;
          const commentId = item.comment_id || item.id;
          const prevSameAuthor = prevItem?.actor_id === item.actor_id && prevItem?.item_type === 'comment' && !prevItem?.is_deleted;
          const hasAttachments = !isDeleted && item.attachments && item.attachments.length > 0;
          const isImageOnly = !isDeleted && hasAttachments && item.attachments!.every(a => isImageMime(a.mime_type)) && (!item.content || item.content === '📎');
          const isInternal = item.is_internal === true;
          const actions = messageActionsFor(isInternal);

          return (
            <div key={item.id}>
              {showDateSep && (
                <ChatDaySeparator date={item.created_at} t={t} lang={i18n.language || 'he'} />
              )}
              <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} ${prevSameAuthor ? 'mt-0.5' : 'mt-3'}`}>
                <div className="dt-row max-w-[80%] md:max-w-[65%] relative">
                  {(!prevSameAuthor || isClaudeMarked(claudeMarks, commentId)) && !isDeleted && (
                    <div className={`flex items-center gap-2 mb-0.5 px-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                      <span className="text-xs font-medium text-surface-700">
                        {isOwnMessage ? t('chat.you') : item.actor_name || t('chat.deletedEmployee')}
                      </span>
                      {isClaudeMarked(claudeMarks, commentId) && <ClaudeBadge />}
                    </div>
                  )}
                  {!isDeleted && (
                    <div className="absolute top-1 end-1 z-10">
                      <MenuButton
                        label={t('ticketDetail.composer.messageActions', { name: isOwnMessage ? t('chat.you') : (item.actor_name || t('chat.deletedEmployee')) })}
                        triggerClassName="dt-reveal btn-icon size-7 min-h-0 min-w-0 rounded-full bg-surface-0 border border-surface-200 text-surface-600 hover:text-surface-900"
                        items={[
                          ...(actions.reply ? [{ key: 'reply', label: t('ticketDetail.composer.reply'), icon: <CornerUpLeft size={16} strokeWidth={1.75} />, onSelect: () => { setComposeMode('reply'); if (taskModes && taskMode === 'internal') setTaskMode(DEFAULT_TASK_COMPOSE_MODE); setReplyingTo(item); textareaRef.current?.focus(); } }] : []),
                          ...(actions.forward ? [{ key: 'forward', label: t('ticketDetail.composer.forward'), icon: <Forward size={16} strokeWidth={1.75} />, onSelect: () => setForwardCommentId(commentId) }] : []),
                          ...(isOwnMessage ? [{ key: 'delete', label: t('ticketDetail.composer.deleteMessage'), icon: <Trash2 size={16} strokeWidth={1.75} />, danger: true, separatorBefore: true, onSelect: () => setDeleteTarget({ id: commentId }) }] : []),
                        ]}
                      >
                        <MoreVertical size={14} aria-hidden="true" />
                      </MenuButton>
                    </div>
                  )}

                  {isDeleted ? (
                    <div className={`rounded-xl px-3 py-2 text-sm italic text-surface-600 border border-dashed border-surface-300 ${
                      isOwnMessage ? 'rounded-se-xs' : 'rounded-ss-xs'
                    }`}>
                      {t('chat.messageDeleted')}
                    </div>
                  ) : isImageOnly ? (
                    <div className={`rounded-xl overflow-hidden border ${isInternal ? 'border-warning-300 bg-warning-50' : 'border-surface-200'} ${isOwnMessage ? 'rounded-se-xs' : 'rounded-ss-xs'}`}>
                      {isInternal && <div className="px-2 pt-1"><InternalNoteTag t={t} /></div>}
                      {item.attachments!.map((att) => (
                        <ImageOnlyAttachment key={att.id} att={att} />
                      ))}
                      <div className={`px-2 py-1 text-xs ${isOwnMessage ? 'bg-primary-50' : 'bg-surface-0'} text-surface-600 flex items-center justify-end gap-1 tabular-nums`}>
                        <span>{formatMessageTime(item.created_at)}</span>
                        {isOwnMessage && <MessageReceiptTicks status={item.receipt_status} t={t} />}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`rounded-xl px-3 py-2 pe-8 text-body-long relative ${
                        isInternal
                          ? `bg-warning-50 text-surface-900 border border-warning-300 ${isOwnMessage ? 'rounded-se-xs' : 'rounded-ss-xs'}`
                          : isOwnMessage
                            ? 'bg-primary-50 text-surface-900 rounded-se-xs'
                            : 'bg-surface-0 text-surface-900 border border-surface-200 rounded-ss-xs'
                      }`}
                    >
                      {isInternal && <InternalNoteTag t={t} />}
                      {item.reply_to_content && (
                        <div className={`mb-2 px-2 py-1.5 rounded-xs border-s-2 text-xs ${
                          isOwnMessage ? 'bg-surface-0/70 border-primary-500' : 'bg-surface-50 border-surface-400'
                        }`}>
                          <span className="font-semibold block text-xs">{item.reply_to_author_name || t('chat.deletedEmployee')}</span>
                          <span className="line-clamp-3 text-surface-700">
                            {item.reply_to_content.replace(MENTION_TOKEN_RE, (_full, _id, title) => `#${title}`)}
                          </span>
                        </div>
                      )}
                      {item.content && item.content !== '📎' && (
                        <p className="whitespace-pre-wrap break-words">
                          {renderCommentContent(item.content, handleMentionClick, t)}
                          {/* D-125 PM-01: the "סיום משימה" comment (also in the work records) */}
                          {item.comment_kind === 'task_finished' && <> <PendingDecision id="PM-01" /></>}
                        </p>
                      )}
                      {hasAttachments && (
                        <div className="mt-1.5 space-y-1.5">
                          {item.attachments!.map((att) => (
                            <AttachmentBubble key={att.id} att={att} isOwn={isOwnMessage} />
                          ))}
                        </div>
                      )}
                      <div className={`flex items-center gap-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                        <span className="text-xs text-surface-600 tabular-nums">{formatMessageTime(item.created_at)}</span>
                        {isOwnMessage && <CheckCheck size={12} className="text-info-700" aria-hidden="true" />}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
        {newPending > 0 && <NewMessagePill onClick={jumpToNewest} />}
      </div>

      {/* QA-D316-317 (390): on a service ticket the row ("אני על זה" / "העברה") goes INTO the dock: outside it (FQ-040)
          it ended under the docked composer and could not be pressed. Other kinds keep FQ-040 as is. */}
      {extrasOutsideDock && !ticketComposer && <div className="bg-surface-0 px-2 pt-2">{replyActionsEl}</div>}
      {/* Composer (docked to the screen bottom on phones when `dockComposer`) */}
      {/* D-265 round 4: a task of an engagement in the archive is read only - no composer. */}
      {!readOnly && (
      <div ref={composerRef} className={dockComposer ? 'composer-dock bg-surface-50 max-md:rounded-b-xl' : 'contents'}>
        {dockComposer && <div data-timer-slot className="empty:hidden md:hidden" />}

        {actionError && (
          <p className="text-xs text-danger-700 mb-2 px-2" role="alert">{actionError}</p>
        )}

        {replyingTo && (
          <div className="flex items-center gap-2 mx-2 mb-2 px-3 py-2 bg-surface-0 rounded-lg border border-surface-200 border-s-2 border-s-primary-500">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-surface-800">{t('ticketDetail.composer.replyingTo')} {replyingTo.actor_name || t('chat.deletedEmployee')}</p>
              <p className="text-xs text-surface-600 truncate">{replyingTo.content}</p>
            </div>
            <button type="button" onClick={() => setReplyingTo(null)} className="text-surface-500 hover:text-surface-600" aria-label={t('chat.cancelReply')}>
              <X size={16} />
            </button>
          </div>
        )}
      <div
        className={`bg-surface-0 border-t border-surface-200 px-2 md:px-3 py-2 ${dockComposer ? 'max-md:rounded-b-xl' : ''} ${dragOver ? 'ring-2 ring-focus ring-inset' : ''}`}
        onDragEnter={(e) => { if (dragHasFiles(e.dataTransfer)) { e.preventDefault(); dragDepth.current += 1; setDragOver(true); } }}
        onDragOver={(e) => { if (dragHasFiles(e.dataTransfer)) e.preventDefault(); }}
        onDragLeave={() => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragOver(false); }}
        onDrop={(e) => {
          dragDepth.current = 0; setDragOver(false);
          const fs = filesFromTransfer(e.dataTransfer);
          if (fs.length) { e.preventDefault(); addFiles(fs); }
        }}
      >
        {dragOver && <p className="mb-1 text-center text-xs font-medium text-accent-text" role="status">{t('media.dropHere')}</p>}
        {extrasOutsideDock && !ticketComposer ? replyNoticeEl : replyExtras}
        {/* P-37 (Q-174 / Q-175 / Q-178) — the composer's "טיימר מענה". */}
        {replyBoxShown && !ticketComposer && (
          <label className="mb-1.5 inline-flex items-center gap-1.5 px-1 text-xs text-surface-700 min-h-8 cursor-pointer" data-testid="reply-box">
            <input
              type="checkbox"
              className="size-4 accent-current"
              checked={replyBoxOn}
              onChange={(e) => setReplyBoxOn(e.target.checked)}
            />
            {t('replyTimer.box')}
            {taskType === 'discussion' && <PendingDecision id="PM-24" />}{/* DQ-448: D-193 / Q-171 */}
          </label>
        )}
        {/* Pending files preview */}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-2 p-2 bg-surface-50 rounded-lg">
            {pendingFiles.map((f, i) => (
              <PendingImagePreview key={i} file={f} onRemove={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))} />
            ))}
          </div>
        )}

        {showCapture && (
          <div className="mb-2 rounded-lg border border-surface-200 bg-surface-0 p-2">
            <MediaCapture
              onAdd={(fs) => { addFiles(fs); setShowCapture(false); }}
              remaining={Math.max(0, 10 - pendingFiles.length)}
              maxBytes={MEDIA_MAX_BYTES}
              disabled={submitting}
              onPendingChange={setCapturePending}
            />
            <button type="button" className="btn btn-ghost btn-sm mt-1" disabled={capturePending} onClick={() => setShowCapture(false)}>{t('common.close')}</button>
          </div>
        )}

        {uploadInfo && (
          <div className="mb-2 px-3 py-1.5 bg-surface-50 rounded-lg" role="status">
            <p className="text-xs text-surface-700">{t('media.uploadingFile', { n: uploadInfo.n, total: uploadInfo.total, pct: uploadInfo.pct })}</p>
            <div className="mt-1 h-1 rounded bg-surface-200" aria-hidden="true"><div className="h-1 rounded bg-accent-hover" style={{ width: `${uploadInfo.pct}%` }} /></div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-danger-50 rounded-lg" role="alert">
            <X size={14} className="text-danger-600" aria-hidden="true" />
            <p className="text-xs text-danger-700">{error}</p>
          </div>
        )}

        {taskModes && (
          <div role="radiogroup" aria-label={t('ticketDetail.composer.mode.label')} className="mb-1.5 flex flex-wrap gap-1.5 px-1">
            {TASK_COMPOSE_MODES.map((m) => {
              const active = taskMode === m;
              const tone = m === 'internal'
                ? (active ? 'bg-warning-100 border-warning-400 text-warning-900' : 'bg-surface-0 border-surface-200 text-surface-700 hover:bg-warning-50')
                : (active ? 'bg-primary-50 border-primary-300 text-primary-900' : 'bg-surface-0 border-surface-200 text-surface-700 hover:bg-surface-50');
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => { setTaskMode(m); if (m === 'internal') setReplyingTo(null); textareaRef.current?.focus(); }}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium min-h-8 transition-colors ${tone}`}
                >
                  {m === 'internal' ? <Lock size={14} aria-hidden="true" /> : m === 'question' ? <HelpCircle size={14} aria-hidden="true" /> : <MessageCircle size={14} aria-hidden="true" />}
                  {t(`ticketDetail.composer.taskMode.${m}`)}
                </button>
              );
            })}
          </div>
        )}
        {offerInternal && !taskModes && (
          <div role="radiogroup" aria-label={t('ticketDetail.composer.mode.label')} className={ticketComposer ? 'flex flex-wrap items-center rounded-t-xl border border-b-0 border-surface-200 bg-surface-50 px-1' : 'mb-1.5 flex gap-1.5 px-1'}>
            {(['reply', 'internal'] as const).map((m) => {
              const active = composeMode === m;
              const tone = ticketComposer
                ? `border-b-2 ${active ? (m === 'internal' ? 'border-warning-500 text-warning-900' : 'border-accent text-accent-text') : 'border-transparent text-surface-700 hover:text-surface-900'}`
                : m === 'internal'
                ? (active ? 'bg-warning-100 border-warning-400 text-warning-900' : 'bg-surface-0 border-surface-200 text-surface-700 hover:bg-warning-50')
                : (active ? 'bg-primary-50 border-primary-300 text-primary-900' : 'bg-surface-0 border-surface-200 text-surface-700 hover:bg-surface-50');
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => { setComposeMode(m); if (m === 'internal') setReplyingTo(null); textareaRef.current?.focus(); }}
                  className={ticketComposer
                    ? `inline-flex items-center gap-1.5 px-3 text-xs font-medium min-h-9 transition-colors ${tone}`
                    : `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium min-h-8 transition-colors ${tone}`}
                >
                  {m === 'internal' ? <Lock size={14} aria-hidden="true" /> : <MessageCircle size={14} aria-hidden="true" />}
                  {t(`ticketDetail.composer.mode.${m}`)}
                </button>
              );
            })}
            {ticketComposer && (
              <p className="ms-auto flex min-w-0 items-center gap-1.5 px-2 text-xs text-text-2" data-testid="composer-to">
                {draftSaved && <span>{t('ticketDetail.composer.draftSaved')} ·</span>}
                {internalMode ? (
                  <span className="truncate font-medium text-warning-800">{t('ticketDetail.composer.mode.internalOnly')}</span>
                ) : (
                  <span className="truncate">
                    {audience?.kind === 'customer' && audience.name
                      ? <>{t('ticketDetail.composer.audience.customer')} · <bdi className="font-medium text-text">{audience.name}</bdi></>
                      : t('ticketDetail.composer.audience.customer')}
                  </span>
                )}
              </p>
            )}
          </div>
        )}
        {ticketComposer && <p id={`composer-hint-${ticketId}`} className="sr-only">{sendKeyText}</p>}

        {(audience || !touch) && !ticketComposer && (
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-1 text-xs text-text-2">
            {internalMode ? (
              <p className="flex min-w-0 items-center gap-1.5 font-medium text-warning-800">
                <Lock size={14} className="shrink-0" aria-hidden="true" />
                <span className="truncate">{t('ticketDetail.composer.mode.internalAudience')}</span>
              </p>
            ) : taskModes ? (
              <p className="flex min-w-0 items-center gap-1.5">
                {taskMode === 'question'
                  ? <HelpCircle size={14} className="shrink-0" aria-hidden="true" />
                  : <Users size={14} className="shrink-0" aria-hidden="true" />}
                <span className="truncate">
                  {taskMode === 'question'
                    ? (questionLine?.blocked
                      ? <span className="font-medium text-danger-700">{t('ticketDetail.composer.taskMode.questionNoRecipient')}</span>
                      : questionLine
                        ? <>{t('ticketDetail.composer.taskMode.questionAudience')} · <bdi className="font-medium text-text">{questionLine.names}</bdi></>
                        : t('ticketDetail.composer.taskMode.questionAudience'))
                    : t('ticketDetail.composer.taskMode.teamAudience')}
                </span>
              </p>
            ) : audience ? (
              <p className="flex min-w-0 items-center gap-1.5">
                {audience.kind === 'team'
                  ? <Headphones size={14} className="shrink-0" aria-hidden="true" />
                  : <Users size={14} className="shrink-0" aria-hidden="true" />}
                <span className="truncate">
                  {audience.kind === 'customer' && (audience.name
                    ? <>{t('ticketDetail.composer.audience.customer')} · <bdi className="font-medium text-text">{audience.name}</bdi></>
                    : t('ticketDetail.composer.audience.customer'))}
                  {audience.kind === 'discussion' && t(isConversation ? 'ticketDetail.composer.audience.conversation' : 'ticketDetail.composer.audience.discussion')}
                  {audience.kind === 'team' && t('ticketDetail.composer.audience.team')}
                </span>
              </p>
            ) : <span />}
            <p id={`composer-hint-${ticketId}`} className="flex items-center gap-2">
              {draftSaved && <span>{t('ticketDetail.composer.draftSaved')}</span>}
              {draftSaved && !touch && <span aria-hidden="true">·</span>}
              {!touch && (
                <>
                  <span>
                    <bdi dir="ltr" className="font-medium tabular-nums">{sendKey === 'enter' ? 'Enter' : `${modKeyLabel()}+Enter`}</bdi>
                    {' '}{t('ticketDetail.composer.sendKey.toSend')}
                  </span>
                  <button
                    type="button"
                    onClick={toggleSendKey}
                    className="rounded-xs font-medium text-accent-text hover:underline underline-offset-2"
                    aria-label={sendKey === 'enter' ? t('ticketDetail.composer.sendKey.switchToMod', { mod: modKeyLabel() }) : t('ticketDetail.composer.sendKey.switchToEnter')}
                  >
                    {t('ticketDetail.composer.sendKey.change')}
                  </button>
                </>
              )}
            </p>
          </div>
        )}

        <div className={ticketComposer ? 'flex flex-wrap items-center gap-1 rounded-b-xl border border-surface-200 bg-surface-0 p-1.5' : 'flex items-end gap-1.5'} data-testid={ticketComposer ? 'ticket-composer-box' : undefined}>
          {/* Attach (D6: its own button, beside the voice button) */}
          <div ref={attachRef} className="relative">
            <button
              ref={attachButtonRef}
              type="button"
              onClick={() => setShowAttachMenu(!showAttachMenu)}
              aria-expanded={showAttachMenu}
              aria-controls={showAttachMenu ? `attach-menu-${ticketId}` : undefined}
              className="p-2.5 text-surface-600 hover:text-surface-900 transition-colors rounded-full hover:bg-surface-100"
              aria-label={t('ticketDetail.composer.attach')}
              title={t('ticketDetail.composer.attach')}
            >
              <Plus size={24} className={`transition-transform ${showAttachMenu ? 'rotate-45' : ''}`} />
            </button>
            {showAttachMenu && (
              <>
                <div id={`attach-menu-${ticketId}`} role="group" aria-label={t('ticketDetail.composer.attach')} className="absolute bottom-12 start-0 dropdown-menu py-1 z-20 min-w-44">
                  <button type="button" onClick={() => { galleryRef.current?.click(); }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-surface-700 hover:bg-surface-50 transition-colors">
                    <Image size={18} className="text-surface-500" aria-hidden="true" />
                    <span>{t('chat.gallery')}</span>
                  </button>
                  <CameraCapture
                    variant="menuItem"
                    onCapture={(file) => {
                      addFiles([file]);
                    }}
                  />
                  <button type="button" onClick={() => { fileRef.current?.click(); }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-surface-700 hover:bg-surface-50 transition-colors">
                    <FileText size={18} className="text-surface-500" aria-hidden="true" />
                    <span>{t('chat.document')}</span>
                  </button>
                  <button type="button" onClick={() => { setShowCapture(true); setShowAttachMenu(false); }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-surface-700 hover:bg-surface-50 transition-colors">
                    <MonitorUp size={18} className="text-surface-500" aria-hidden="true" />
                    <span>{t('media.screenOrShot')}</span>
                  </button>
                </div>
              </>
            )}
          </div>

          <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <input ref={galleryRef} type="file" accept={IMAGE_ACCEPT} multiple className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          {showVoiceRecorder ? (
            <div className={ticketComposer ? 'order-first basis-full' : 'flex-1'}>
              <VoiceRecorder
                fullWidth
                autoStart
                confirmBeforeSend
                onRecordingComplete={(file) => {
                  setShowVoiceRecorder(false);
                  setPendingFiles((prev) => [...prev, file]);
                }}
                onCancel={() => setShowVoiceRecorder(false)}
              />
            </div>
          ) : (
            <>
              <div className={ticketComposer ? 'relative order-first basis-full' : 'flex-1 relative'}>
                <QuickRepliesBar
                  content={content}
                  onSelect={(text) => {
                    setContent(text);
                    autoResizeTextarea();
                  }}
                  highlightIdx={quickReplyHighlight}
                  onHover={setQuickReplyHighlight}
                />
                {mention !== null && (
                  <MentionAutocomplete
                    items={mentionItems}
                    highlight={mentionHighlight}
                    loading={mentionLoading}
                    onPick={insertMention}
                    onHover={setMentionHighlight}
                    t={t}
                  />
                )}
                <textarea
                  ref={textareaRef}
                  className={`w-full resize-none border rounded-xl px-4 py-2.5 text-sm placeholder:text-surface-500 focus:outline-none focus:border-focus focus:ring-1 focus:ring-focus min-h-touch max-h-32 ${
                    internalMode ? 'bg-warning-50 border-warning-300' : 'bg-surface-50 border-surface-200 focus:bg-surface-0'
                  }`}
                  placeholder={t(internalMode ? 'ticketDetail.composer.internalPlaceholder' : 'ticketDetail.composer.placeholder')}
                  aria-label={t('ticketDetail.composer.messageLabel')}
                  aria-describedby={`composer-hint-${ticketId}`}
                  value={content}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.trim()) markReplyStarted();
                    else if (pendingFiles.length === 0) replyStartedAt.current = null;
                    setContent(v);
                    autoResizeTextarea();
                    const caret = e.target.selectionStart ?? v.length;
                    const trig = detectMentionTrigger(v, caret);
                    setMention(trig);
                  }}
                  onKeyDown={(e) => {
                    if (mention !== null && mentionItems.length > 0) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setMentionHighlight((h) => (h + 1) % mentionItems.length);
                        return;
                      }
                      if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setMentionHighlight((h) => (h - 1 + mentionItems.length) % mentionItems.length);
                        return;
                      }
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        insertMention(mentionItems[mentionHighlight]);
                        return;
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setMention(null);
                        return;
                      }
                    }
                    if (quickRepliesOpen && quickRepliesFiltered.length > 0) {
                      quickRepliesKeyDown(e);
                      return;
                    }
                    if (isSendKeystroke(e.nativeEvent, sendKey)) {
                      e.preventDefault();
                      void submit();
                    }
                  }}
                  onKeyUp={(e) => {
                    const target = e.currentTarget;
                    const caret = target.selectionStart ?? target.value.length;
                    const trig = detectMentionTrigger(target.value, caret);
                    setMention(trig);
                  }}
                  onClick={(e) => {
                    const target = e.currentTarget;
                    const caret = target.selectionStart ?? target.value.length;
                    const trig = detectMentionTrigger(target.value, caret);
                    setMention(trig);
                  }}
                  onPaste={(e) => {
                    // R-02: Ctrl+V of a screenshot / file attaches it; pasting text (even from Word, which also carries an image) stays text.
                    if (e.clipboardData.getData('text/plain')) return;
                    const fs = filesFromTransfer(e.clipboardData);
                    if (fs.length) { e.preventDefault(); addFiles(fs); }
                  }}
                  rows={ticketComposer ? 2 : 1}
                  dir="auto"
                />
              </div>

              <button
                type="button"
                onClick={() => { markReplyStarted(); setShowVoiceRecorder(true); }}
                className="p-2.5 text-surface-600 hover:text-surface-900 transition-colors rounded-full hover:bg-surface-100 flex-shrink-0"
                aria-label={t('voice.record')}
                title={t('voice.record')}
              >
                <Mic size={24} />
              </button>
            </>
          )}

          {ticketComposer && replyBoxShown && (
            <label className="inline-flex min-h-8 cursor-pointer items-center gap-1.5 rounded-full bg-surface-100 px-2.5 text-xs text-surface-700" data-testid="reply-box">
              <input
                type="checkbox"
                className="size-4 accent-current"
                checked={replyBoxOn}
                onChange={(e) => setReplyBoxOn(e.target.checked)}
              />
              {t('replyTimer.box')}
            </label>
          )}
          {ticketComposer && <span className="flex-1" aria-hidden="true" />}
          {ticketComposer && !touch && (
            <button
              type="button"
              onClick={toggleSendKey}
              className="rounded-xs px-1 text-xs font-medium text-accent-text hover:underline underline-offset-2"
              aria-label={sendKey === 'enter' ? t('ticketDetail.composer.sendKey.switchToMod', { mod: modKeyLabel() }) : t('ticketDetail.composer.sendKey.switchToEnter')}
              title={sendKeyText}
            >
              {t('ticketDetail.composer.sendKey.change')}
            </button>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={submitting || questionBlocked || (!content.trim() && pendingFiles.length === 0)}
            className="p-2.5 bg-accent-hover text-white rounded-full hover:bg-accent-pressed disabled:opacity-40 transition-[background-color,transform] duration-150 flex-shrink-0 active:scale-95"
            aria-label={t('chat.sendMessage')}
            title={ticketComposer && !touch ? sendKeyText : undefined}
          >
            <Send size={18} className="rtl-mirror" aria-hidden="true" />
          </button>
        </div>
      </div>
      </div>
      )}

      <ChatForwardModal
        open={!!forwardCommentId}
        sourceTicketId={ticketId}
        commentId={forwardCommentId || ''}
        onClose={() => setForwardCommentId(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
        }}
        t={t}
      />
      {deleteTarget && (
        <ConfirmModal
          title={t('ticketDetail.composer.deleteMessage')}
          description={t('chat.deleteConfirm')}
          confirmLabel={t('common.delete')}
          onConfirm={() => void handleDeleteMessage()}
          onCancel={() => setDeleteTarget(null)}
          danger
        />
      )}
    </div>
  );
}
